/**
 * Logs Page
 *
 * Real-time log viewer with filtering capabilities.
 * Uses LogViewer component with WebSocket streaming.
 */

import { useState, type CSSProperties } from 'react';
import { tokens, LogViewer } from '../local-components';
import { useLogEvents, type LogFilters } from '../hooks/useLogEvents';
import { useAgent } from '../hooks/useAgent';
import { getGrafanaBaseUrl } from '../lib/grafana';

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[6],
  height: '100%',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes['2xl'],
  fontWeight: tokens.fontWeights.bold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: tokens.spacing[2],
};

const buttonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.void,
  backgroundColor: tokens.colors.mint,
  border: 'none',
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
  cursor: 'pointer',
};

const buttonSecondaryStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: 'transparent',
  color: tokens.colors.textMuted,
  border: `1px solid ${tokens.colors.border}`,
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: tokens.colors.surface,
  color: tokens.colors.textMuted,
  cursor: 'not-allowed',
};

const statsStyle: CSSProperties = {
  display: 'flex',
  gap: tokens.spacing[4],
  padding: tokens.spacing[3],
  backgroundColor: tokens.colors.surface,
  borderRadius: tokens.radii.md,
  fontSize: tokens.fontSizes.sm,
};

const statItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[2],
};

const statLabelStyle: CSSProperties = {
  color: tokens.colors.textMuted,
};

const statValueStyle: CSSProperties = {
  color: tokens.colors.textPrimary,
  fontWeight: tokens.fontWeights.medium,
};

const loadingStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  padding: tokens.spacing[6],
  textAlign: 'center',
};

const errorStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.danger,
  padding: tokens.spacing[4],
  backgroundColor: 'rgba(255, 71, 87, 0.1)',
  border: `1px solid ${tokens.colors.danger}`,
  borderRadius: tokens.radii.md,
};

const viewerContainerStyle: CSSProperties = {
  flex: 1,
  minHeight: 0, // Allow shrinking
};

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function Logs() {
  const { isConnected } = useAgent();
  const [autoScroll, setAutoScroll] = useState(true);

  const {
    logs,
    isLoading,
    error,
    filters,
    setFilters,
    availableEnvironments,
    availableArtifacts,
    refreshLogs,
    clearLogs,
    hasMore,
    loadMore,
  } = useLogEvents({
    maxLogs: 1000,
    autoFetch: true,
    filters: null,
  });

  const handleFiltersChange = (newFilters: LogFilters) => {
    setFilters(newFilters);
  };

  const handleRefresh = () => {
    refreshLogs();
  };

  const handleClear = () => {
    clearLogs();
  };

  const toggleAutoScroll = () => {
    setAutoScroll((prev) => !prev);
  };

  // Count logs by level
  const levelCounts = logs.reduce(
    (acc, log) => {
      acc[log.level] = acc[log.level] + 1;
      return acc;
    },
    { debug: 0, info: 0, warn: 0, error: 0 },
  );

  if (!isConnected) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Logs</h1>
        <div style={loadingStyle}>Connecting to agent...</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Logs</h1>
        <div style={actionsStyle}>
          <button
            onClick={toggleAutoScroll}
            style={buttonSecondaryStyle}
            title={autoScroll ? 'Disable auto-scroll' : 'Enable auto-scroll'}
          >
            {autoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}
          </button>
          <button onClick={handleClear} style={buttonSecondaryStyle}>
            Clear
          </button>
          <button
            onClick={handleRefresh}
            disabled={!isConnected || isLoading}
            style={isConnected && !isLoading ? buttonStyle : buttonDisabledStyle}
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={statsStyle}>
        <div style={statItemStyle}>
          <span style={statLabelStyle}>Total:</span>
          <span style={statValueStyle}>{logs.length}</span>
        </div>
        <div style={statItemStyle}>
          <span style={{ ...statLabelStyle, color: tokens.colors.danger }}>Errors:</span>
          <span
            style={{
              ...statValueStyle,
              color: levelCounts.error > 0 ? tokens.colors.danger : undefined,
            }}
          >
            {levelCounts.error}
          </span>
        </div>
        <div style={statItemStyle}>
          <span style={{ ...statLabelStyle, color: tokens.colors.warning }}>Warnings:</span>
          <span
            style={{
              ...statValueStyle,
              color: levelCounts.warn > 0 ? tokens.colors.warning : undefined,
            }}
          >
            {levelCounts.warn}
          </span>
        </div>
        {hasMore && (
          <button onClick={loadMore} style={buttonSecondaryStyle}>
            Load More
          </button>
        )}
      </div>

      {/* Error */}
      {error && <div style={errorStyle}>Error: {error}</div>}

      {/* Log Viewer */}
      <div style={viewerContainerStyle}>
        <LogViewer
          logs={logs}
          autoScroll={autoScroll}
          maxLines={1000}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          availableEnvironments={availableEnvironments}
          availableArtifacts={availableArtifacts}
          grafanaUrl={getGrafanaBaseUrl()}
          style={{ height: '100%', minHeight: '500px' }}
        />
      </div>
    </div>
  );
}
